import { useState, useCallback, useRef } from 'react';
import { Copy, Check } from 'lucide-react';

interface CodeBlockProps {
  className?: string;
  children?: React.ReactNode;
  node?: any;
}

// Parse code block meta string
// e.g., 'javascript title="app.js" showLineNumbers {1-3,5}'
interface ParsedMeta {
  language: string;
  title?: string;
  showLineNumbers: boolean;
  highlightLines: number[];
}

function parseMeta(className: string = '', meta: string = ''): ParsedMeta {
  const language = className.replace('language-', '') || 'text';
  let title: string | undefined;
  let showLineNumbers = false;
  const highlightLines: number[] = [];

  // Parse title="xxx" or title='xxx'
  const titleMatch = meta.match(/title=["']([^"']+)["']/);
  if (titleMatch) {
    title = titleMatch[1];
  }

  // Parse showLineNumbers
  if (meta.includes('showLineNumbers')) {
    showLineNumbers = true;
  }

  // Parse highlight lines {1,3-5,7}
  const highlightMatch = meta.match(/\{([^}]+)\}/);
  if (highlightMatch) {
    const ranges = highlightMatch[1].split(',');
    ranges.forEach((range) => {
      range = range.trim();
      if (range.includes('-')) {
        const [start, end] = range.split('-').map((n) => parseInt(n.trim(), 10));
        if (!isNaN(start) && !isNaN(end)) {
          for (let i = start; i <= end; i++) {
            highlightLines.push(i);
          }
        }
      } else {
        const num = parseInt(range, 10);
        if (!isNaN(num)) {
          highlightLines.push(num);
        }
      }
    });
  }

  return { language, title, showLineNumbers, highlightLines };
}

export function CodeBlock({ className, children, node }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef<HTMLElement>(null);

  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : 'text';
  const meta = node?.data?.meta || '';
  const { title, showLineNumbers, highlightLines } = parseMeta(className, meta);

  // Get code content
  const codeContent = String(children).replace(/\n$/, '');

  // Split into lines
  const lines = codeContent.split('\n');

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(codeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [codeContent]);

  // Determine if this is a code block (has newlines) or inline code
  const isCodeBlock = codeContent.includes('\n') || (node?.position?.start?.line !== node?.position?.end?.line);

  // Inline code
  if (!isCodeBlock && !className) {
    return (
      <code className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono text-red-600 dark:text-red-400">
        {children}
      </code>
    );
  }

  // Code block
  return (
    <div className="relative group my-4 rounded-lg border border-border overflow-hidden">
      {/* Header with language and optional title */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {title ? (
            <>
              <span className="font-medium text-foreground">{title}</span>
              <span className="text-border">|</span>
              <span>{language}</span>
            </>
          ) : (
            <span className="font-medium uppercase">{language}</span>
          )}
        </div>
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          title={copied ? '已复制' : '复制代码'}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Code content with optional line numbers */}
      <div className="overflow-x-auto">
        <div className="flex">
          {/* Line numbers */}
          {showLineNumbers && (
            <div className="flex-shrink-0 py-4 pr-2 pl-4 text-right select-none border-r border-border bg-muted/30">
              {lines.map((_, i) => (
                <div
                  key={i}
                  className={`text-xs leading-6 ${
                    highlightLines.includes(i + 1)
                      ? 'text-primary font-medium'
                      : 'text-muted-foreground'
                  }`}
                >
                  {i + 1}
                </div>
              ))}
            </div>
          )}

          {/* Code */}
          <pre className={`flex-1 p-4 overflow-x-auto ${showLineNumbers ? '' : ''}`}>
            <code ref={codeRef} className={`language-${language}`}>
              {lines.map((line, i) => (
                <div
                  key={i}
                  className={`leading-6 ${
                    highlightLines.includes(i + 1)
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 -mx-4 px-4 border-l-2 border-yellow-500'
                      : ''
                  }`}
                >
                  {line || ' '}
                </div>
              ))}
            </code>
          </pre>
        </div>
      </div>
    </div>
  );
}

// Pre component wrapper
export function PreBlock({ children }: { children?: React.ReactNode }) {
  // Check if children is a code element
  if (children && typeof children === 'object' && 'type' in children && children.type === 'code') {
    // This will be handled by CodeBlock
    return <>{children}</>;
  }

  // Fallback for other pre content
  return (
    <pre className="my-4 p-4 bg-muted rounded-lg overflow-x-auto">
      {children}
    </pre>
  );
}
